import { useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Users, Plus, Trash2, Pencil, Save } from 'lucide-react'
import { api, type AdminSchoolStaff } from '../lib/api'
import { queries } from '../lib/queries'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Badge } from '../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Skeleton } from '../components/ui/skeleton'

export const Route = createFileRoute('/admin/schools/$id')({
  head: () => ({
    meta: [{ title: 'School Detail | Admin | Unbreakable' }],
  }),
  component: AdminSchoolDetailPage,
})

function AdminSchoolDetailPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showAddStaffDialog, setShowAddStaffDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [staffToRemove, setStaffToRemove] = useState<AdminSchoolStaff | null>(null)
  const [staffFormData, setStaffFormData] = useState({ email: '', role: 'staff' as 'admin' | 'staff', name: '' })
  const [editFormData, setEditFormData] = useState({ name: '', slug: '', timezone: '', seatLimit: 0 })
  const [formError, setFormError] = useState<string | null>(null)

  const { data: schoolsData, isLoading: schoolLoading } = useQuery({
    queryKey: queries.admin.schools(),
    queryFn: () => api.getAdminSchools(),
  })
  const school = schoolsData?.schools.find((s) => s.id === id)

  const { data: staffData, isLoading: staffLoading } = useQuery({
    queryKey: queries.admin.schoolStaff(id),
    queryFn: () => api.getAdminSchoolStaff(id),
  })
  const staff = staffData?.staff ?? []

  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; slug?: string; timezone?: string; seatLimit?: number }) =>
      api.updateAdminSchool(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.admin.schools() })
      setShowEditDialog(false)
    },
    onError: (err) => setFormError(err instanceof Error ? err.message : 'Failed to update school'),
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteAdminSchool(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.admin.schools() })
      navigate({ to: '/admin/schools' })
    },
    onError: (err) => setFormError(err instanceof Error ? err.message : 'Failed to delete school'),
  })

  const [inviteSent, setInviteSent] = useState(false)
  const addStaffMutation = useMutation({
    mutationFn: (data: { email: string; role: 'admin' | 'staff'; name?: string }) =>
      api.addAdminSchoolStaff(id, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queries.admin.schoolStaff(id) })
      queryClient.invalidateQueries({ queryKey: queries.admin.schools() })
      setShowAddStaffDialog(false)
      setStaffFormData({ email: '', role: 'staff', name: '' })
      setFormError(null)
      if (result.invited) setInviteSent(true)
    },
    onError: (err) => setFormError(err instanceof Error ? err.message : 'Failed to add staff'),
  })

  const removeStaffMutation = useMutation({
    mutationFn: (staffId: string) => api.removeAdminSchoolStaff(id, staffId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.admin.schoolStaff(id) })
      queryClient.invalidateQueries({ queryKey: queries.admin.schools() })
      setStaffToRemove(null)
    },
  })

  const openEditDialog = () => {
    if (!school) return
    setEditFormData({ name: school.name, slug: school.slug, timezone: school.timezone || '', seatLimit: school.seatLimit })
    setFormError(null)
    setShowEditDialog(true)
  }

  if (schoolLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  if (!school) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">School not found.</p>
        <Link to="/admin/schools">
          <Button variant="outline" className="mt-4"><ArrowLeft className="h-4 w-4 mr-1" />Back to Schools</Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/admin/schools">
          <Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900">{school.name}</h1>
          <p className="text-gray-500 font-mono text-sm">{school.slug}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openEditDialog}><Pencil className="h-4 w-4 mr-1" />Edit</Button>
          <Button variant="outline" className="text-red-600 hover:text-red-700" onClick={() => setShowDeleteDialog(true)}><Trash2 className="h-4 w-4 mr-1" />Delete</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{staff.length}</div><p className="text-sm text-gray-500">Staff Members</p></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{school.seatUsage ?? 0} / {school.seatLimit}</div><p className="text-sm text-gray-500">Seats Used</p></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-sm font-medium">{school.timezone || '—'}</div><p className="text-sm text-gray-500">Timezone</p></CardContent></Card>
        <Card><CardContent className="pt-6"><Badge variant={school.billingStatus === 'active' ? 'default' : 'outline'}>{school.billingStatus || 'No billing'}</Badge><p className="text-sm text-gray-500 mt-1">Billing</p></CardContent></Card>
      </div>

      {/* Staff Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" />Staff Members</CardTitle>
          <Button size="sm" onClick={() => { setFormError(null); setShowAddStaffDialog(true) }}>
            <Plus className="h-4 w-4 mr-1" />Add Staff
          </Button>
        </CardHeader>
        <CardContent>
          {staffLoading ? (
            <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : staff.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>No staff members yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {staff.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell className="font-medium">{member.email}</TableCell>
                    <TableCell>{member.name || '—'}</TableCell>
                    <TableCell><Badge variant={member.role === 'admin' ? 'default' : 'outline'}>{member.role}</Badge></TableCell>
                    <TableCell>
                      <Badge variant={member.status === 'active' ? 'default' : 'outline'}>
                        {member.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setStaffToRemove(member)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Invite sent toast */}
      {inviteSent && (
        <div className="fixed bottom-4 right-4 z-50 rounded-lg bg-green-50 border border-green-200 p-4 shadow-lg">
          <p className="text-sm text-green-800 font-medium">Invite email sent! They'll receive a link to set up their account.</p>
          <button className="mt-1 text-xs text-green-600 underline" onClick={() => setInviteSent(false)}>Dismiss</button>
        </div>
      )}

      {/* Add Staff Dialog */}
      <Dialog open={showAddStaffDialog} onOpenChange={(open) => { setShowAddStaffDialog(open); if (!open) { setFormError(null); setStaffFormData({ email: '', role: 'staff', name: '' }) } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Staff Member</DialogTitle>
            <DialogDescription>Enter their email. If they don't have an account yet, we'll send them an invite to set one up.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {formError && <div className="rounded-lg bg-red-50 border border-red-200 p-3"><p className="text-sm text-red-700">{formError}</p></div>}
            <div><Label>Email *</Label><Input type="email" value={staffFormData.email} onChange={(e) => setStaffFormData({ ...staffFormData, email: e.target.value })} placeholder="staff@school.edu" /></div>
            <div><Label>Name</Label><Input value={staffFormData.name} onChange={(e) => setStaffFormData({ ...staffFormData, name: e.target.value })} placeholder="Jane Smith" /></div>
            <div>
              <Label>Role</Label>
              <Select value={staffFormData.role} onValueChange={(v) => setStaffFormData({ ...staffFormData, role: v as 'admin' | 'staff' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                onClick={() => addStaffMutation.mutate({ email: staffFormData.email, role: staffFormData.role, name: staffFormData.name || undefined })}
                disabled={addStaffMutation.isPending || !staffFormData.email}
                className="flex-1"
              >
                {addStaffMutation.isPending ? 'Adding...' : 'Add Staff'}
              </Button>
              <Button variant="outline" onClick={() => setShowAddStaffDialog(false)} className="flex-1">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit School Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => { setShowEditDialog(open); if (!open) setFormError(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit School</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {formError && <div className="rounded-lg bg-red-50 border border-red-200 p-3"><p className="text-sm text-red-700">{formError}</p></div>}
            <div><Label>Name</Label><Input value={editFormData.name} onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })} /></div>
            <div><Label>Slug</Label><Input value={editFormData.slug} onChange={(e) => setEditFormData({ ...editFormData, slug: e.target.value })} className="font-mono" /></div>
            <div><Label>Timezone</Label><Input value={editFormData.timezone} onChange={(e) => setEditFormData({ ...editFormData, timezone: e.target.value })} /></div>
            <div><Label>Seat Limit</Label><Input type="number" value={editFormData.seatLimit} onChange={(e) => setEditFormData({ ...editFormData, seatLimit: parseInt(e.target.value, 10) || 0 })} /></div>
            <div className="flex gap-2 pt-2">
              <Button onClick={() => updateMutation.mutate(editFormData)} disabled={updateMutation.isPending} className="flex-1">
                <Save className="h-4 w-4 mr-1" />{updateMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
              <Button variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove Staff Confirmation */}
      <AlertDialog open={!!staffToRemove} onOpenChange={(open) => { if (!open) setStaffToRemove(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Staff Member</AlertDialogTitle>
            <AlertDialogDescription>
              Remove <strong>{staffToRemove?.email}</strong> from this school? They will lose access to the school dashboard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => staffToRemove && removeStaffMutation.mutate(staffToRemove.id)}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete School Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete School</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{school.name}</strong>? This action cannot be undone. The school must have no active enrollments.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Deleting...' : 'Delete School'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
